import React, { FC, useEffect } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { useMutation } from 'react-relay';
import { Formik, Form } from 'formik';

import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';

import Copyright from 'components/Copyright';
import TextField from 'components/TextField';
import Checkbox from 'components/Checkbox';

import { useNotification } from 'components/Notifications';

import logo from 'assets/tedo-logo-blue.png';

import { LoginMutation } from '__generated__/LoginMutation.graphql';
import Button from 'components/Button';

type FormValues = {
  username: string;
  password: string;
  remember: boolean;
};

const initial = {
  username: '',
  password: '',
  remember: false,
};

const defaultFailMessage = 'Nepavyko prisijungti';

const Login: FC = () => {
  const { pop } = useNotification();

  const [login, loading] = useMutation<LoginMutation>(graphql`
    mutation LoginMutation($username: String!, $password: String!) {
      token(username: $username, password: $password) {
        accessToken
      }
    }
  `);

  const handleSubmit = ({ username, password, remember }: FormValues) => {
    login({
      variables: { username, password },
      onCompleted: (response, errors) => {
        if (errors) {
          pop(errors[0]?.message ?? defaultFailMessage);
          return;
        }

        const { token } = response;
        if (!token?.accessToken) return;

        if (remember) {
          localStorage.setItem('token', token.accessToken);
        } else {
          sessionStorage.setItem('token', token.accessToken);
        }

        window.location.reload();
      },
      onError: error => {
        pop(error.message ?? defaultFailMessage);
      },
    });
  };

  useEffect(() => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
  }, []);

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
        <img src={logo} alt="TEDO.lt" />

        <Typography component="h1" variant="h5">
          Administracija
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Formik<FormValues> initialValues={initial} onSubmit={handleSubmit}>
            <Form noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="El. paštas"
                name="username"
                autoComplete="username"
                autoFocus
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Slaptažodis"
                type="password"
                id="password"
                autoComplete="current-password"
              />

              <FormControlLabel
                control={
                  <Checkbox name="remember" value={true} color="primary" />
                }
                label="Prisiminti mane"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                loading={loading}
                sx={{ mt: 3, mb: 2 }}
                progressProps={{ color: 'inherit' }}>
                Pirmyn
              </Button>
            </Form>
          </Formik>

          <Grid container>
            <Grid item xs>
              <Link href="#" variant="body2">
                Pamiršote slaptažodį?
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Copyright sx={{ mt: 8, mb: 4 }} />
    </Container>
  );
};

export default Login;
